import { useState } from 'react';
import { StatusFilter, type TemplateListStatus } from '../automations-tab/templates-manager/types';
import { toTargetedTenantsFilter } from '../automations-tab/templates.types';
import { useSyncAutomationTemplates, useUpdateAutomationTemplate } from '../useAutomationTemplates';
import { useAutomationEditorContext } from './AutomationEditorContext';
import { getAutomationTemplateSaveButtonLabel } from './automationTemplateSaveButtonLabel';
import { useAutomationTemplateTenantSelection } from './useAutomationTemplateTenantSelection';
import { useSyncStatusHandshakeLock } from './useSyncStatusHandshakeLock';

/**
 * Orchestrates everything EditAutomationTemplateContent renders: the tenant-selection data, the
 * derived template status/availability copy, the sync/disabled state, and the save handlers. Keeps
 * the component presentational and lets the toolbar controls read `isSyncing` from context rather
 * than receiving it as a drilled prop.
 */
export function useEditAutomationTemplateContent() {
  const { editor } = useAutomationEditorContext();
  const { state, markClean, template, undoReorder } = editor;
  const {
    tenantSelection,
    localContents,
    hasReorderedOrAdded,
    hasStepsNeedingSetup,
    isDirty,
    dirtySteps,
  } = state;

  const updateMutation = useUpdateAutomationTemplate();
  const syncMutation = useSyncAutomationTemplates();
  const [showReorderWarning, setShowReorderWarning] = useState(false);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);

  const {
    conflicts,
    isValidating,
    tenantRowData,
    syncingTenantCount,
    jobSyncActive,
    isLoading: isLoadingTenants,
  } = useAutomationTemplateTenantSelection({
    templateId: template.id,
    tenantSelectionType: tenantSelection.selectionType,
    selectedTenantIds: tenantSelection.selectedTenantIds,
  });

  const syncStatusHandshakeLock = useSyncStatusHandshakeLock(jobSyncActive, {
    isPending: syncMutation.isPending,
    isError: syncMutation.isError,
    isSuccess: syncMutation.isSuccess,
  });

  const isSyncing =
    jobSyncActive || syncMutation.isPending || updateMutation.isPending || syncStatusHandshakeLock;

  const totalTenantCount = Object.keys(tenantRowData).length;
  const isDraft =
    tenantSelection.selectionType === 'selected' && tenantSelection.selectedTenantIds.length === 0;
  const anyTenantInUse = Object.values(tenantRowData).some(row => row.isActive);

  let templateStatus: TemplateListStatus = StatusFilter.Available;
  if (isDraft) {
    templateStatus = StatusFilter.Draft;
  } else if (anyTenantInUse) {
    templateStatus = StatusFilter.Active;
  }

  let availabilitySummary: string;
  if (isDraft) {
    availabilitySummary = 'Not synced to any tenant yet';
  } else if (tenantSelection.selectionType === 'all') {
    availabilitySummary = `Available to all ${totalTenantCount} tenants (and new ones)`;
  } else {
    availabilitySummary = `Available to ${tenantSelection.selectedTenantIds.length} of ${totalTenantCount} tenants`;
  }

  const handleSave = () => {
    const selectionTypeAtSave = tenantSelection.selectionType;
    const selectedIdsAtSave = tenantSelection.selectedTenantIds;
    const contentsAreDirty =
      dirtySteps.trigger || dirtySteps.settings || dirtySteps.actions.size > 0 || hasReorderedOrAdded;

    updateMutation.mutate(
      {
        id: template.id,
        params: {
          // A tenant-only save must not write the contents snapshot. Generation can replace
          // those contents while this editor is open, and posting them would wipe that copy.
          ...(contentsAreDirty ? { contents: localContents } : {}),
          targetedTenantsFilter: toTargetedTenantsFilter(selectionTypeAtSave, selectedIdsAtSave),
        },
      },
      {
        onSuccess: () => {
          markClean();
          syncMutation.mutate({ templateId: template.id, silent: true });
        },
      },
    );
  };

  const handleSaveClick = hasReorderedOrAdded ? () => setShowReorderWarning(true) : handleSave;

  const saveDisabled =
    !isDirty ||
    hasStepsNeedingSetup ||
    isValidating ||
    updateMutation.isPending ||
    syncMutation.isPending ||
    isSyncing;

  let saveTitle: string | undefined;
  if (hasStepsNeedingSetup) {
    saveTitle = 'Please configure all steps before saving';
  } else if (isValidating) {
    saveTitle = 'Validating tenant segment compatibility…';
  } else if (isSyncing) {
    saveTitle = 'Sync in progress';
  } else if (saveDisabled && !isDirty) {
    saveTitle = 'No changes to save';
  }

  const saveButtonLabel = getAutomationTemplateSaveButtonLabel({
    isUpdatePending: updateMutation.isPending,
    jobSyncActive,
    syncingTenantCount,
    isSyncMutationPending: syncMutation.isPending,
  });

  return {
    name: localContents.name,
    description: localContents.description,
    templateStatus,
    availabilitySummary,
    isDraft,
    isSyncing,
    tenantRowData,
    conflicts,
    isValidating,
    isLoadingTenants,
    hasReorderedOrAdded,
    undoReorder,
    tenantSelection,
    saveButtonLabel,
    saveDisabled,
    saveTitle,
    handleSaveClick,
    handleSave,
    showReorderWarning,
    setShowReorderWarning,
    tenantDialogOpen,
    openTenantDialog: () => setTenantDialogOpen(true),
    closeTenantDialog: () => setTenantDialogOpen(false),
  };
}
